import { inject, Injectable, signal } from '@angular/core';
import {
  GS1ModalRef,
  GS1ModalService,
  ModalCloseReason,
  ModalCloseResult,
  isModalConfirmed
} from '@gs1france/gs1-components-library';
import { TranslateService } from '@ngx-translate/core';
import { catchError, of, take, tap } from 'rxjs';
import { Interaction } from '../models/info-perso.model';
import { ProfileService } from './profile.service';
import { StorageService } from './storage.service';

interface OnboardingStep {
  title: string;
  imageUrl: string;
  back?: string;
  cancel: string;
  confirm: string;
  body: string;
}

@Injectable()
export class OnboardingService {
  private modalService = inject(GS1ModalService);
  private translateService = inject(TranslateService);
  private profileService = inject(ProfileService);
  private storageService = inject(StorageService);

  private onboardingModalOpened = signal(false);
  private onboardingModalRef: GS1ModalRef;

  open() {
    const onboardingInteraction = this.getOnboardingInteraction();
    if (!onboardingInteraction?.mustDisplay || this.onboardingModalOpened()) return;

    this.onboardingModalOpened.set(true);
    this.openWizardModal();
  }

  private openWizardModal(initialStep = 1) {
    let currentStep = initialStep;
    const steps = this.getOnboardingSteps();
    const modalRef = this.modalService.open({
      ...steps[currentStep - 1],
      currentStep,
      totalSteps: steps.length,
      showStepIndicator: true,
      width: '720px',
      height: '760px',
      disableClose: true,
      closeButton: false
    });

    this.onboardingModalRef = modalRef;

    const displayStep = (step: number): void => {
      currentStep = step;
      const config = steps[currentStep - 1];
      const dialogRef = modalRef.getMatDialogRef();

      Object.assign(dialogRef.componentInstance.data, {
        title: config.title,
        body: config.body
      });
      dialogRef.componentRef?.setInput('imageUrl', config.imageUrl);
      dialogRef.componentRef?.setInput('currentStep', currentStep);
      modalRef.updateState({
        backText: config.back,
        cancelText: config.cancel,
        confirmText: config.confirm
      });
      dialogRef.componentRef?.changeDetectorRef.markForCheck();
    };

    modalRef.back().subscribe(() => displayStep(currentStep - 1));
    modalRef.afterClosed().subscribe((result: ModalCloseResult<unknown>) => {
      if (isModalConfirmed(result) && currentStep < steps.length) {
        this.openWizardModal(currentStep + 1);
      } else if (result?.reason === ModalCloseReason.CONFIRM) {
        this.completeOnboarding();
      } else if (result?.reason === ModalCloseReason.CANCEL) {
        this.closeOnboardingModal();
      }
    });
  }

  private closeOnboardingModal() {
    this.onboardingModalRef?.close();
  }

  private completeOnboarding() {
    const onboardingInteraction = this.getOnboardingInteraction();
    if (!onboardingInteraction) return;

    this.profileService
      .acceptInteraction(onboardingInteraction.id)
      .pipe(
        take(1),
        tap(() => {
          const user = this.storageService.userValue;
          this.storageService.setUser({
            ...user,
            onboardingDone: true,
            interactions: user.interactions?.map((interaction: Interaction) =>
              interaction.id === onboardingInteraction.id ? { ...interaction, mustDisplay: false } : interaction
            )
          });
        }),
        catchError((error) => {
          console.error('Onboarding completion failed', error);
          return of(null);
        })
      )
      .subscribe();
  }

  private getOnboardingInteraction(): Interaction | undefined {
    return this.storageService.userValue?.interactions?.find(({ code }: Interaction) => code === 'WELCOME_V1');
  }

  private getOnboardingSteps(): OnboardingStep[] {
    const skip = this.translateService.instant('onboarding.actions.skip');
    const next = this.translateService.instant('onboarding.actions.next');
    const back = this.translateService.instant('onboarding.actions.back');

    return [
      {
        title: this.translateService.instant('onboarding.steps.activity.title'),
        imageUrl: 'assets/images/onboarding/onboarding-1.jpg',
        cancel: skip,
        confirm: next,
        body: this.translateService.instant('onboarding.steps.activity.description')
      },
      {
        title: this.translateService.instant('onboarding.steps.codes.title'),
        imageUrl: 'assets/images/onboarding/onboarding-2.jpg',
        back,
        cancel: skip,
        confirm: next,
        body: this.translateService.instant('onboarding.steps.codes.description')
      },
      {
        title: this.translateService.instant('onboarding.steps.collaboration.title'),
        imageUrl: 'assets/images/onboarding/onboarding-3.jpg',
        back,
        cancel: skip,
        confirm: next,
        body: this.translateService.instant('onboarding.steps.collaboration.description')
      },
      {
        title: this.translateService.instant('onboarding.steps.administration.title'),
        imageUrl: 'assets/images/onboarding/onboarding-4.jpg',
        back,
        cancel: skip,
        confirm: this.translateService.instant('onboarding.actions.finish'),
        body: this.translateService.instant('onboarding.steps.administration.description')
      }
    ];
  }
}
